import { useState } from "react";
import PasswordField from "../../../components/inputfields/PasswordField";
import { motion } from "framer-motion";
import useApi from "../../../services/useApi";
import { useDispatch } from "react-redux";
import { showToast } from "../../../components/toastMessage/toastSlice";
import useTranslation from "../../../components/CustomHooks/useTranslation";
import { getFirefoxCssFix } from "../../../components/commonfunctions/getFirefoxCssFix";
import Image from "../../../components/images/Image";
import Button from "../../../components/buttons/Button";
import { fetchAuthSession, signOut } from "aws-amplify/auth";
import { useNavigate } from "react-router-dom";

interface UpdatePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function UpdatePasswordModal({ isOpen, onClose }: UpdatePasswordModalProps) {
  const InitialState: { [key: string]: string } = {
    current_password: "",
    new_password: "",
    confirm_password: "",
  };
  const [state, setState] = useState(InitialState);
  const [error, setError] = useState<{ [key: string]: string }>(InitialState);
  const [loading, setLoading] = useState(false);
  const { PatchAPI } = useApi();
  const dispatch = useDispatch();
  const { translate, notEnglish } = useTranslation({});
  const navigate = useNavigate();
  const PASSWORD_REQUIREMENTS = [
    {
      test: (password: string) => password.length >= 6 && password.length <= 16,
      message: "Minimum 6 and maximum 16 Characters",
    },
    {
      test: (password: string) =>
        /[!@#$%^&*(),.?":{}|<>_+=\-\[\]\\/;:'"~`]/.test(password),
      message: "Atleast 1 Special Character (e.g. !@#$)",
    },
    {
      test: (password: string) => /\d/.test(password),
      message: "Atleast 1 Number",
    },
    {
      test: (password: string) => /[A-Z]/.test(password),
      message: "Atleast 1 Uppercase",
    },
    {
      test: (password: string) => /[a-z]/.test(password),
      message: "Atleast 1 Lowercase",
    },
  ];

  const validateMandatoryFields = (obj: { [key: string]: string }, fields: string[]) => {
    const err: { [key: string]: string } = {};
    fields.forEach((field) => {
      if (!obj[field]) err[field] = "This field is required";
    });
    return err;
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target;
    setState((prev) => ({ ...prev, [id]: value }));
    setError((prev) => ({ ...prev, [id]: "" }));
  };

  const handleSubmit = async () => {
    const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^\w\s]).{6,16}$/;
    setError(InitialState);
    let mandatoryFields = ["current_password", "new_password", "confirm_password"];
    let error = validateMandatoryFields(state, mandatoryFields);
    if (Object.keys(error).length > 0) {
      setError(error);
      return;
    }
    if (state?.new_password !== state?.confirm_password) {
      setError({
        confirm_password: "Passwords do not match", // pragma: allowlist secret
      });
      return;
    }
    if (state?.new_password === state?.current_password) {
      setError({
        new_password: "New password cannot be the same as current password", // pragma: allowlist secret
      });
      return;
    }
    if (!passwordRegex.test(state?.new_password)) {
      setError({
        new_password: "Invalid password", // pragma: allowlist secret
      });
      return;
    }
    setLoading(true);
    // You may need to update the endpoint and payload as per your backend
    let body = {
      current_password: state.current_password, // pragma: allowlist secret
      password: state.new_password, // pragma: allowlist secret
      confirm_password: state.confirm_password, // pragma: allowlist secret
    };
    // Fetch authToken and pass as x-access-token header
    let response;
    try {
      // Only pass the useXAccessToken flag as the fifth argument
      response = await PatchAPI(
        "admins/update-password",
        body,
        true,
        undefined,
        true // useXAccessToken
      );
    } catch (err) {
      response = { error: true, data: { message: "Failed to get auth token" } };
    }
    if (
      response.data.status === 400 &&
      response.data.response.data.message === "Invalid Password" // pragma: allowlist secret
    ) {
      setError({ current_password: response.data.response.data.message });
    } else if (!response.error) {
      dispatch(
        showToast({
          type: "success",
          message: translate("Password updated successfully"),
        })
      );
      onClose();
      setTimeout(() => {
        navigate('/')
      }, 2000);
    } else {
      dispatch(
        showToast({
          type: "error",
          message:
            response.data.response.data.message || "Something went wrong",
        })
      );
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  const setNewPassword = [
    {
      labelName: "Current Password",
      id: "current_password",
      value: state?.current_password,
    },
    {
      labelName: "New Password",
      id: "new_password",
      value: state?.new_password,
    },
    {
      labelName: "Confirm Password",
      id: "confirm_password",
      value: state?.confirm_password,
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
      <div className="relative bg-white rounded-lg shadow-lg p-6 sm:p-10 w-full max-w-[460px] mx-auto">
        <Image
          src="close"
          onClick={onClose}
          className="absolute top-6 right-6 cursor-pointer text-xl font-bold z-10"
        />
        <div className="flex flex-col items-center justify-center w-full mb-5 mt-3">
          {/* <Image src="zood_pay_sidebar_img" /> */}
          <h2 className="text-xl sm:text-2xl font-semibold mb-4">
            {translate("Update Password")}
          </h2>
          <p className="text-secondary_text break-words text-center text-sm sm:text-base">
            Please enter your current password and set a new one to update your
            credentials.
          </p>
        </div>
        <div className="flex flex-col items-center justify-center w-full">
          {setNewPassword?.map((item, index) => (
            <div
              className={`w-full ${error?.[item?.id] ? "mb-[6px]" : "mb-[32px]"}`}
              key={index}
            >
              <PasswordField
                inputItem={{ labelName: item?.labelName, id: item?.id }}
                errorMessage={error?.[item?.id]}
                onChange={(e) => handleInputChange(e)}
                required={true}
                isUpdatePassword={true}
                maxLength={16}
              />
              {item.id === "new_password" && state?.new_password.length > 0 && (
                <div className="mt-3 w-full">
                  <p className="mb-3">
                    {translate("Password should contain:")}
                  </p>
                  <div
                    className={`max-h-[80px] pl-1 pr-5 overflow-y-scroll overflow-x-hidden ${getFirefoxCssFix(
                      "scroll"
                    )} scroll-auto`}
                  >
                    <ul
                      className={`list-disc ${notEnglish ? "mr-4" : "ml-4"} `}
                    >
                      {PASSWORD_REQUIREMENTS.map(({ test, message }) => (
                        <motion.li
                          key={message}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{
                            opacity: 1,
                            x: 0,
                            color: state.new_password
                              ? test(state.new_password)
                                ? "#28a745"
                                : "#000000"
                              : "#000000",
                          }}
                          transition={{ duration: 0.5 }}
                          className="mb-2 body2"
                        >
                          {translate(message)}
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ))}
          <Button
            buttonOnclick={handleSubmit}
            buttonName="Update"
            className="mt-3 w-full bg-p_primary text-white rounded-lg font-semibold disabled:opacity-60"
            loading={loading}
            buttonHight={'h-[56px]'}
            testId="update-password"
          />
        </div>
      </div>
    </div>
  );
} 